<!--
  ConfirmDialog — Accessible confirmation dialog using Radix Vue primitives.

  Usage:
    <ConfirmDialog title="Delete item?" description="This cannot be undone." @confirm="doIt">
      <template #trigger>
        <button class="btn btn-danger">Delete</button>
      </template>
    </ConfirmDialog>

  Radix handles: focus trapping, Escape to close, aria-modal, aria-labelledby/describedby.
  Other candidates for Radix adoption: DropdownMenu (NotificationCenter), Tooltip (SchedulePanel), Popover.
-->
<script setup>
import {
  DialogRoot,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from 'radix-vue'

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: null },
  confirmLabel: { type: String, default: 'Confirm' },
  cancelLabel: { type: String, default: 'Cancel' },
  dangerous: { type: Boolean, default: true },
})

const emit = defineEmits(['confirm'])

const open = defineModel('open', { default: false })
</script>

<template>
  <DialogRoot v-model:open="open">
    <DialogTrigger v-if="$slots.trigger" as-child>
      <slot name="trigger" />
    </DialogTrigger>

    <DialogPortal>
      <DialogOverlay class="dialog-overlay" />
      <DialogContent class="dialog-content">
        <DialogTitle class="dialog-title">{{ title }}</DialogTitle>
        <DialogDescription v-if="description" class="dialog-description">
          {{ description }}
        </DialogDescription>

        <div class="dialog-actions">
          <DialogClose as-child>
            <button class="btn btn-ghost">{{ cancelLabel }}</button>
          </DialogClose>
          <DialogClose as-child>
            <button
              :class="['btn', dangerous ? 'btn-danger' : 'btn-primary']"
              @click="emit('confirm')"
            >
              {{ confirmLabel }}
            </button>
          </DialogClose>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 200;
  animation: overlay-in var(--transition-fast) ease;
}

.dialog-content {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 201;
  background: var(--color-surface-raised);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 1.5rem;
  width: min(92vw, 420px);
  animation: content-in var(--transition-fast) ease;
}

.dialog-title {
  margin: 0 0 0.4rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--color-text);
}

.dialog-description {
  margin: 0 0 1.25rem;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

@keyframes overlay-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@keyframes content-in {
  from { opacity: 0; transform: translate(-50%, -48%); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
}
</style>
